import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ENVIRONMENT,
  PaymentUrlResponse,
  PortalUrlResponse,
  RefreshTokenResponse,
  SsoLoginRequest,
  SsoLoginResponse,
  SsoReturnResponse,
  SwitchBorrowerRequest,
  SwitchBorrowerResponse,
} from '@ld.consumer-portal/models';
import { firstValueFrom } from 'rxjs';

export type {
  PaymentUrlResponse,
  PortalUrlResponse,
  RefreshTokenResponse,
  SsoLoginRequest,
  SsoLoginResponse,
  SsoReturnResponse,
  SwitchBorrowerRequest,
  SwitchBorrowerResponse,
} from '@ld.consumer-portal/models';
import { LoggingService, ThemeService } from '@ld.consumer-portal/utils';

@Injectable({
  providedIn: 'root',
})
export class SsoAuthService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly loggingService = inject(LoggingService);
  private readonly themeService = inject(ThemeService);

  private get cpApiUrl(): string {
    return this.env.appSettings.cpApiUrl || '';
  }

  private get apiNamespace(): string {
    return this.env.appSettings.apiNamespace || 'api/v1';
  }

  async login(request: SsoLoginRequest): Promise<SsoLoginResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/auth/sso`;

    const b2cToken = localStorage.getItem('b2c_token');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'LD-ReqTraceID': this.generateDashlessUuid(),
      MacVisitorId: this.getOrCreateVisitorId(),
      'authorization-provider': this.env.appSettings.useCustomerIdentityAuth ? 'Entra' : 'CP2',
      ...(this.env.appSettings.useCustomerIdentityAuth ? { 'X-Partner-Id': this.themeService.getXPartnerId() } : {}),
      Authorization: `Bearer ${request.ssoToken}`,
      ...(b2cToken ? { B2CToken: b2cToken } : {}),
      ...(this.env.appSettings.cpApimSubscriptionKey
        ? { 'Apim-CP-Provider-Subscription': this.env.appSettings.cpApimSubscriptionKey }
        : {}),
      ...(this.env.appSettings.ocpApimSubscriptionKey
        ? { 'Ocp-Apim-Subscription-Key': this.env.appSettings.ocpApimSubscriptionKey }
        : {}),
    });

    try {
      return await firstValueFrom(this.http.post<SsoLoginResponse>(url, request, { headers }));
    } catch (error: unknown) {
      this.loggingService.logException(error, 'SSO login error');
      throw new Error(`SSO login failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async refresh(): Promise<RefreshTokenResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/auth/refresh`;

    try {
      return await firstValueFrom(this.http.post<RefreshTokenResponse>(url, {}));
    } catch (error: unknown) {
      this.loggingService.logException(error, 'Token refresh error');
      throw new Error(`Token refresh failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async switchBorrower(request: SwitchBorrowerRequest): Promise<SwitchBorrowerResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/auth/switch`;

    try {
      return await firstValueFrom(this.http.post<SwitchBorrowerResponse>(url, request));
    } catch (error: unknown) {
      this.loggingService.logException(error, 'Switch borrower error');
      throw error;
    }
  }

  async check(): Promise<SsoLoginResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/auth/check`;

    try {
      return await firstValueFrom(this.http.get<SsoLoginResponse>(url));
    } catch (error: unknown) {
      this.loggingService.logException(error, 'Auth check error');
      throw new Error(`Auth check failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async generateSsoReturnToken(): Promise<SsoReturnResponse> {

    const baseUrl = this.cpApiUrl;
    const url = `${baseUrl}/${this.apiNamespace}/auth/ula`;

    const cpToken = localStorage.getItem('cp_token');
    const b2cToken = localStorage.getItem('b2c_token');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'LD-ReqTraceID': this.generateDashlessUuid(),
      'authorization-provider': this.env.appSettings.useCustomerIdentityAuth ? 'Entra' : 'CP2',
      ...(this.env.appSettings.useCustomerIdentityAuth ? { 'X-Partner-Id': this.themeService.getXPartnerId() } : {}),
      ...(b2cToken ? { Authorization: `Bearer ${b2cToken}` } : {}),
      ...(this.env.appSettings.cpApimSubscriptionKey
        ? { 'Apim-CP-Provider-Subscription': this.env.appSettings.cpApimSubscriptionKey }
        : {}),
      ...(this.env.appSettings.ocpApimSubscriptionKey
        ? { 'Ocp-Apim-Subscription-Key': this.env.appSettings.ocpApimSubscriptionKey }
        : {}),
    });

    try {
      const body = cpToken ? { authToken: cpToken } : {};
      return await firstValueFrom(this.http.post<SsoReturnResponse>(url, body, { headers }));
    } catch (error: unknown) {
      this.loggingService.logException(error, 'Generate SSO return token error');
      throw new Error(`Generate SSO return token failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async exchangeUlaForMlaSession(ulaToken: string): Promise<string> {
    const baseUrl = this.env.appSettings.mlaApiUrl;
    if (!baseUrl) {
      throw new Error('mlaApiUrl not configured in environment');
    }

    const normalizedBase = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    const url = `${normalizedBase}api/v1/session/sso`;

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: ulaToken }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`MLA SSO login failed (${response.status}): ${errorText}`);
    }

    const data: { success: boolean; data?: { token?: string } } = await response.json();
    if (!data.success || !data.data?.token) {
      throw new Error(`MLA SSO login response invalid: ${JSON.stringify(data)}`);
    }

    return data.data.token;
  }

  async getPaymentUrl(): Promise<PaymentUrlResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/auth/payment`;

    try {
      return await firstValueFrom(this.http.post<PaymentUrlResponse>(url, {}));
    } catch (error: unknown) {
      this.loggingService.logException(error, 'Get payment URL error');
      throw new Error(`Get payment URL failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async getInsurancePortalUrl(): Promise<PortalUrlResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/auth/insurance`;

    try {
      return await firstValueFrom(this.http.post<PortalUrlResponse>(url, {}));
    } catch (error: unknown) {
      this.loggingService.logException(error, 'Get insurance portal URL error');
      throw new Error(`Get insurance portal URL failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private generateDashlessUuid(): string {
    return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  private readonly LOCAL_STORAGE_VISITOR_KEY = 'Session_MacVisitorId';

  private getOrCreateVisitorId(): string {
    const visitorId = localStorage.getItem(this.LOCAL_STORAGE_VISITOR_KEY);
    if (visitorId) {
      return visitorId;
    }

    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15).toUpperCase();
    const newVisitorId = `${timestamp}${random}`;
    localStorage.setItem(this.LOCAL_STORAGE_VISITOR_KEY, newVisitorId);
    return newVisitorId;
  }
}
